export const MANUAL_ENTRY_PLACEHOLDER =
	"Manual entry required. Add this in the content before you add it here.";

export const MANUAL_TOOLTIP_REASON =
	"This value could not be inferred from the page. Add it in visible page content first, then update your schema.";

function propertyKeyFromLine(text) {
	const match = String(text).match(/"([^"]+)"\s*:/);
	return match ? match[1].toLowerCase() : "";
}

/** Map a diff line to the API `could_not_infer` label for tooltip copy. */
export function resolveCouldNotInferLabelForLine(text, couldNotInfer) {
	if (!couldNotInfer?.length || !text?.includes(MANUAL_ENTRY_PLACEHOLDER)) return null;

	const key = propertyKeyFromLine(text);
	if (key) {
		const direct = couldNotInfer.find((label) =>
			String(label).toLowerCase().includes(key)
		);
		if (direct) return direct;

		if (/price|currency/.test(key)) {
			return couldNotInfer.find((label) => /price|offer|currency/i.test(label));
		}
		if (/rating|review/.test(key)) {
			return couldNotInfer.find((label) => /aggregaterating|rating|review/i.test(label));
		}
		if (/datepublished/.test(key)) {
			return couldNotInfer.find((label) => /datepublished/i.test(label));
		}
		if (/datemodified/.test(key)) {
			return couldNotInfer.find((label) => /datemodified/i.test(label));
		}
		if (/downloadurl/.test(key)) {
			return couldNotInfer.find((label) => /downloadurl/i.test(label));
		}
		if (/softwareversion/.test(key)) {
			return couldNotInfer.find((label) => /softwareversion/i.test(label));
		}
		if (/logo/.test(key)) {
			return couldNotInfer.find((label) => /\blogo\b/i.test(label));
		}
		if (/sameas/.test(key)) {
			return couldNotInfer.find((label) => /sameas/i.test(label));
		}
	}

	return couldNotInfer.length === 1 ? couldNotInfer[0] : null;
}

export function buildManualTooltipEntry(label) {
	const property = label || "Manual field";
	return {
		action: "manual",
		property,
		reason: MANUAL_TOOLTIP_REASON,
	};
}

function graphNodes(schema, type) {
	const graph = schema?.["@graph"];
	if (!Array.isArray(graph)) return [];
	return graph.filter((node) => {
		const nodeType = node?.["@type"];
		if (nodeType === type) return true;
		return Array.isArray(nodeType) && nodeType.includes(type);
	});
}

/**
 * Clone recommended schema and inject placeholder values for fields listed in could_not_infer.
 * Display-only; does not change stored job data.
 */
export function applyManualRequiredToSchema(recommendedSchema, couldNotInfer) {
	if (!recommendedSchema || !couldNotInfer?.length) return recommendedSchema;

	const clone = JSON.parse(JSON.stringify(recommendedSchema));
	const graph = clone["@graph"];
	if (!Array.isArray(graph)) return clone;

	const webPage = graphNodes(clone, "WebPage")[0];
	const organization = graphNodes(clone, "Organization")[0];
	const service = graphNodes(clone, "Service")[0];
	const software = graphNodes(clone, "SoftwareApplication")[0];

	for (const label of couldNotInfer) {
		const lower = String(label).toLowerCase();

		if (lower.includes("datepublished") && webPage && webPage.datePublished == null) {
			webPage.datePublished = MANUAL_ENTRY_PLACEHOLDER;
		}
		if (lower.includes("datemodified") && webPage && webPage.dateModified == null) {
			webPage.dateModified = MANUAL_ENTRY_PLACEHOLDER;
		}

		if (lower.includes("aggregaterating")) {
			const target = software || webPage || organization || service;
			if (target && target.aggregateRating == null) {
				target.aggregateRating = {
					"@type": "AggregateRating",
					ratingValue: MANUAL_ENTRY_PLACEHOLDER,
					ratingCount: MANUAL_ENTRY_PLACEHOLDER,
				};
			}
		}

		if (lower.includes("price") || lower.includes("offer")) {
			const target = service || software || webPage;
			if (!target) continue;
			if (target.offers == null) {
				target.offers = {
					"@type": "Offer",
					price: MANUAL_ENTRY_PLACEHOLDER,
					priceCurrency: MANUAL_ENTRY_PLACEHOLDER,
				};
			} else if (typeof target.offers === "object" && !Array.isArray(target.offers)) {
				if (target.offers.price == null) target.offers.price = MANUAL_ENTRY_PLACEHOLDER;
				if (target.offers.priceCurrency == null) {
					target.offers.priceCurrency = MANUAL_ENTRY_PLACEHOLDER;
				}
			}
		}

		if (lower.includes("downloadurl") && software && software.downloadUrl == null) {
			software.downloadUrl = MANUAL_ENTRY_PLACEHOLDER;
		}
		if (lower.includes("softwareversion") && software && software.softwareVersion == null) {
			software.softwareVersion = MANUAL_ENTRY_PLACEHOLDER;
		}
		if (lower.includes("sameas") && organization && organization.sameAs == null) {
			organization.sameAs = [MANUAL_ENTRY_PLACEHOLDER];
		}
		if (lower.includes("logo") && organization && organization.logo == null) {
			organization.logo = MANUAL_ENTRY_PLACEHOLDER;
		}
	}

	return clone;
}

export function markManualDiffRows(
	rows,
	couldNotInfer = [],
	placeholder = MANUAL_ENTRY_PLACEHOLDER
) {
	return rows.map((row) => {
		if (!row.text?.includes(placeholder)) return row;
		const label = resolveCouldNotInferLabelForLine(row.text, couldNotInfer);
		const manualEntry = buildManualTooltipEntry(label);
		return {
			...row,
			type: "manual",
			marker: "!",
			isHighlighted: true,
			manualEntry,
		};
	});
}

export function countManualDiffRows(rows, placeholder = MANUAL_ENTRY_PLACEHOLDER) {
	return rows.filter((row) => row.text?.includes(placeholder)).length;
}
